import React, { useState, useEffect, useCallback } from 'react';
import { Collapse, Modal, Upload, message } from 'antd';
import { CancelButton, Container, SaveButton, SpaceBetweenFlex, AttachmentCollapse } from '../../components/Modals/styles';
import TemplateTabDetails from '../../containers/SidebarMenu/TemplateTabDetails';
import { getBase64 } from '../../utils/javascript';
import { v4 as uuidv4 } from 'uuid';
import { uploadAttachment } from '../../utils/services/uploadAPI';
import AttachmentPreviewModal from '../../components/Modals/AttachmentPreviewModal';
import PageElementsProvider from '../../context/pageElementsContext';
import { withTranslation } from 'react-i18next';
import { ADD_EMAIL_ATTACHMENT_MODAL } from '../../components/Modals/translationValues';
import { COMPLEX_EMAIL_CONTEXT_KEYS, useEmailContext } from '../../context/complexEmailContext';

const NAMESPACES = ['ADD_EMAIL_ATTACHMENT_MODAL'];

const { Panel } = Collapse;
const { Dragger } = Upload;

const AddComplexEmailAttachmentModal = ({ visible, onCancel, onUpload, t }) => {
  const [isBottom, setIsBottom] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(undefined);
  const [selectedTemplate, setSelectedTemplate] = useState(undefined);
  const [activeKey, setActiveKey] = useState('1');
  const attachmentSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.ATTACHMENTS], []);
  const [attachments] = useEmailContext(attachmentSelector, 'AddComplexEmailAttachmentModal');

  const isEditableAttached = attachments.length > 0 && attachments[0]?.isEditable;

  useEffect(() => {
    setActiveKey(isEditableAttached ? '2' : '1');
  }, [isEditableAttached]);

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      const url = await getBase64(file);
      const data = { file: file, thumbnail: url, name: file.name, id: uuidv4() };
      let response = await uploadAttachment(data.file, data.name);
      if (response && response.errors && response.errors.length === 0) {
        onSuccess(response.status);
        message.success(t(ADD_EMAIL_ATTACHMENT_MODAL.FILE_UPLOAD_SUCCESS.key, ADD_EMAIL_ATTACHMENT_MODAL.FILE_UPLOAD_SUCCESS.value));
      }
      if (response.id) {
        setUploadedFile({
          id: response.id,
          name: data.name
        });
      }
    } catch (error) {
      onError(error);
      console.error("Failure in upload:: ", error);
      message.error(` ${t(ADD_EMAIL_ATTACHMENT_MODAL.UNABLE_TO_UPLOAD_ERROR_MESSAGE.key, ADD_EMAIL_ATTACHMENT_MODAL.UNABLE_TO_UPLOAD_ERROR_MESSAGE.value)} ${file.name}`);
    }
  };

  const onRemove = () => {
    setUploadedFile(undefined);
  }

  const props = {
    name: 'file',
    accept: ".pdf",
    customRequest,
    onRemove,
    beforeUpload: (file) => {
      const isPDF =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");

      if (!isPDF) {
        message.error(t(ADD_EMAIL_ATTACHMENT_MODAL.ONLY_PDF_ALLOWED.key, ADD_EMAIL_ATTACHMENT_MODAL.ONLY_PDF_ALLOWED.value));
        return false;
      }

      return true;
    }
  };

  const onProceed = async (attachmentName) => {
    if (uploadedFile !== undefined) { // local file upload
      onUpload({
        contentType: "application/pdf",
        storeId: uploadedFile?.id,
        name: uploadedFile?.name
      });
    } else {
      onUpload({
        contentType: 'document',
        templateId: selectedTemplate?.id,
        name: attachmentName,
        isEditable: selectedTemplate?.isEditable,
        templateDetails: Object.assign({}, selectedTemplate)
      })
    }
    setUploadedFile(undefined);
    setSelectedTemplate(undefined);
  };

  const fileList = uploadedFile ? [{
    uid: uploadedFile.id,
    name: uploadedFile.name,
    status: "done"
  }] : [];

  return (
  <PageElementsProvider>
    <Container>
      <Modal
        title={t(ADD_EMAIL_ATTACHMENT_MODAL.EMAIL_ATTACHMENT_TITLE.key, ADD_EMAIL_ATTACHMENT_MODAL.EMAIL_ATTACHMENT_TITLE.value)}
        centered
        maskClosable={false}
        visible={visible}
        width="75vw"
        onCancel={onCancel}
        style={{ padding: '0px !important' }}
        footer={null}
        className='attachment_modal'
      >
        <AttachmentCollapse>
          <Collapse accordion activeKey={activeKey} onChange={(key) => setActiveKey(key)}>
            <Panel header={<div style={{ display: 'flex', alignItems: 'center' }}>
            <span>
              {t(
                ADD_EMAIL_ATTACHMENT_MODAL.CHOOSE_PRINT_HEADER.key,
                ADD_EMAIL_ATTACHMENT_MODAL.CHOOSE_PRINT_HEADER.value
              )}
            </span>
            {isEditableAttached && (
              <span style={{ color: 'red', marginLeft: 8 }}>
                ({t(
                ADD_EMAIL_ATTACHMENT_MODAL.EDITABLE_TEMPLATE_ATTACHED.key,
                ADD_EMAIL_ATTACHMENT_MODAL.EDITABLE_TEMPLATE_ATTACHED.value
              )})
              </span>
            )}
          </div>} key="1" disabled={isEditableAttached}>
              <TemplateTabDetails
                editable={true}
                isSelectionMode={true}
                isBottom={isBottom}
                setIsBottom={setIsBottom}
                onSelect={(templateObj) => {
                  setSelectedTemplate(templateObj);
                }}
                attachmentTemp={true}
              />
            </Panel>
            <Panel header={t(ADD_EMAIL_ATTACHMENT_MODAL.UPLOAD_FILE_HEADER.key, ADD_EMAIL_ATTACHMENT_MODAL.UPLOAD_FILE_HEADER.value)} key="2">
              <Dragger {...props} fileList={fileList}>
                <p className="ant-upload-text">  {t(ADD_EMAIL_ATTACHMENT_MODAL.DRAG_FILE_ACTION.key, ADD_EMAIL_ATTACHMENT_MODAL.DRAG_FILE_ACTION.value)}</p>
              </Dragger>
              {uploadedFile !== undefined && (<SpaceBetweenFlex style={{ marginTop: 10 }}>
                <CancelButton
                  key='cancel'
                  onClick={() => {
                    setSelectedTemplate(undefined);
                  }}
                >
                  {t(ADD_EMAIL_ATTACHMENT_MODAL.CANCEL_BUTTON.key, ADD_EMAIL_ATTACHMENT_MODAL.CANCEL_BUTTON.value)}
                </CancelButton>
                <SaveButton
                  key='submit'
                  onClick={() => onProceed()}
                >
                  {t(ADD_EMAIL_ATTACHMENT_MODAL.ADD_ATTACHMENT_BUTTON.key, ADD_EMAIL_ATTACHMENT_MODAL.ADD_ATTACHMENT_BUTTON.value)}
                </SaveButton>
              </SpaceBetweenFlex>)}
            </Panel>
          </Collapse>
        </AttachmentCollapse>
      </Modal >
      {selectedTemplate && (
        <AttachmentPreviewModal
          visible={true}
          onCancel={() => {
            setSelectedTemplate(undefined);
          }}
          targetTemplate={selectedTemplate}
          currentIndex={0}
          onProceed={onProceed}
        />
      )}
    </Container >
  </PageElementsProvider>
  );
}
export default withTranslation(NAMESPACES)(AddComplexEmailAttachmentModal);